/**
 * Executive dashboard — the direction of the business at a glance.
 *
 * Layout follows the plan: risk-led KPI tiles, disbursed-vs-collected bars,
 * portfolio trend, PAR trends, aging composition, regional comparison,
 * origination funnel, and the cash-flow window.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/lendingCalculations";
import { staffApi } from "@/platform/staffApi";
import { useStaff } from "./StaffShell";

type Row = Record<string, any>;

const BUCKET_COLORS: Record<string, string> = {
  "1-7": "hsl(201 90% 45%)",
  "8-30": "hsl(32 92% 52%)",
  "31-60": "hsl(28 90% 48%)",
  "61-90": "hsl(0 72% 55%)",
  "90+": "hsl(0 72% 42%)",
};

const kwacha = (v: number) => formatCurrency(v);
const compact = (v: number) =>
  v >= 1_000_000 ? `K${(v / 1_000_000).toFixed(1)}M`
  : v >= 1000 ? `K${(v / 1000).toFixed(0)}k`
  : `K${v}`;

function Tile({ label, value, sub, tone, to }: {
  label: string; value: string; sub?: string;
  tone?: "good" | "warn" | "bad"; to?: string;
}) {
  const toneClass = tone === "bad" ? "text-destructive"
    : tone === "warn" ? "text-warning"
    : tone === "good" ? "text-success" : "";
  const card = (
    <Card className={`h-full ${to ? "transition-colors hover:border-primary/40" : ""}`}>
      <CardContent className="pt-5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} aria-label={label}>{card}</Link> : card;
}

export function ExecutiveDashboard() {
  const staff = useStaff();
  const [data, setData] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    staffApi.executive()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load metrics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin" />;
  if (error || !data) {
    return <p className="py-16 text-center text-sm text-destructive">{error}</p>;
  }

  const k = data.kpis;
  const par30 = k.par30;
  const buckets = Object.entries(data.arrears_by_bucket || {})
    .map(([label, amount]) => ({ label, amount: Number(amount) }))
    .filter((b) => b.amount > 0);

  const monthLabel = (ym: string) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1)
      .toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };
  const monthly = (data.monthly_flow || []).map((m: Row) => ({
    month: monthLabel(m.month),
    Disbursed: m.disbursed,
    Collected: m.collected,
  }));

  const trend = (data.trend || []).map((t: Row) => ({
    date: t.date.slice(5),
    Outstanding: t.outstanding,
    "PAR 15": t.par15,
    "PAR 30": t.par30,
    "PAR 90": t.par90,
  }));

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {data.scope?.national ? "All regions" : "Your region"} · as of{" "}
            {new Date(data.as_of).toLocaleDateString()}
          </p>
        </div>
        <p className="text-xs capitalize text-muted-foreground">
          {String(staff?.role || "").split("_").join(" ")}
        </p>
      </header>

      {/* Row 1 — risk first, then the book */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Portfolio at risk (PAR30)"
          value={par30 == null ? "—" : `${par30.toFixed(1)}%`}
          sub={`${kwacha(k.par30_amount || 0)} at risk`}
          tone={par30 == null ? undefined : par30 > 5 ? "bad" : "good"}
          to="/staff/collections"
        />
        <Tile
          label="Outstanding portfolio"
          value={kwacha(k.outstanding)}
          sub={`${k.active_loans} loans · ${k.active_borrowers} borrowers`}
          to="/staff/loans"
        />
        <Tile
          label="Collection rate (month)"
          value={k.collection_rate_mtd == null ? "—" : `${k.collection_rate_mtd.toFixed(1)}%`}
          sub={k.collection_rate_mtd == null ? "nothing due yet"
            : k.collection_rate_healthy ? "healthy (≥98.5%)" : "below the 98.5% threshold"}
          tone={k.collection_rate_mtd == null ? undefined : k.collection_rate_healthy ? "good" : "warn"}
        />
        <Tile
          label="Disbursed this month"
          value={kwacha(k.disbursed_mtd)}
          sub={`${k.disbursed_count_mtd} loans · avg ${k.average_ticket ? kwacha(k.average_ticket) : "—"}`}
        />
      </div>

      {/* Row 2 — what clients owe + early warning */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Clients owe (arrears)" value={kwacha(k.total_arrears)}
              sub="overdue instalments" tone={k.total_arrears > 0 ? "warn" : "good"} />
        <Tile label="First-payment default"
              value={k.first_payment_default == null ? "—" : `${k.first_payment_default.toFixed(1)}%`}
              sub="new loans missing instalment 1"
              tone={k.first_payment_default > 10 ? "bad" : undefined} />
        <Tile label="Expected next 7 days" value={kwacha(data.cash_flow.expected_7d)}
              sub="scheduled collections" />
        <Tile label="Pipeline to disburse" value={kwacha(data.cash_flow.pipeline_to_disburse)}
              sub="approved, awaiting payout" />
      </div>

      {k.collection_rate_mtd != null && !k.collection_rate_healthy && (
        <Card className="border-warning/40">
          <CardContent className="flex items-center gap-3 py-4 text-sm">
            <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
            <p>
              Collection rate is {k.collection_rate_mtd.toFixed(1)}% this month. On short-cycle
              lending, sustained rates below ~98.5% compound into heavy annual losses — check the{" "}
              <Link to="/staff/collections" className="font-medium text-primary underline">
                collections queue
              </Link>.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Direction of the business */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Disbursed vs collected</CardTitle>
            <CardDescription>Money out against money back, by month</CardDescription>
          </CardHeader>
          <CardContent>
            {monthly.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No month-end data yet — snapshots build this over time.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis tickFormatter={compact} fontSize={12} width={60} />
                  <Tooltip formatter={(v: number) => kwacha(v)} />
                  <Legend />
                  <Bar dataKey="Disbursed" fill="hsl(201 90% 40%)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Collected" fill="hsl(162 60% 38%)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Portfolio at risk</CardTitle>
            <CardDescription>PAR 15 / 30 / 90 over time (%)</CardDescription>
          </CardHeader>
          <CardContent>
            {trend.length < 2 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                Trends appear once a few days of snapshots exist.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis unit="%" fontSize={12} width={50} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="PAR 15" stroke="hsl(32 92% 52%)" dot={false} />
                  <Line type="monotone" dataKey="PAR 30" stroke="hsl(0 72% 55%)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="PAR 90" stroke="hsl(0 72% 35%)" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* How much clients owe, by age */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">What clients owe, by age</CardTitle>
            <CardDescription>Arrears split across delinquency buckets</CardDescription>
          </CardHeader>
          <CardContent>
            {buckets.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                Nothing overdue — the book is current.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={buckets} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.3} />
                  <XAxis type="number" tickFormatter={compact} fontSize={12} />
                  <YAxis type="category" dataKey="label" width={60} fontSize={12}
                         tickFormatter={(l) => `${l} days`} />
                  <Tooltip formatter={(v: number) => kwacha(v)} />
                  <Bar dataKey="amount" radius={[0, 3, 3, 0]}>
                    {buckets.map((b) => (
                      <Cell key={b.label} fill={BUCKET_COLORS[b.label] || "hsl(201 90% 40%)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Origination funnel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sales funnel</CardTitle>
            <CardDescription>
              Signup to disbursement
              {data.funnel?.pull_through != null && ` · ${data.funnel.pull_through}% of applications funded`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.funnel?.stages || []).map((s: Row, i: number) => {
              const top = data.funnel.stages[0].count || 1;
              const width = Math.max((s.count / top) * 100, s.count ? 6 : 0);
              return (
                <div key={s.stage}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{s.stage}</span>
                    <span className="tabular-nums">
                      {s.count}
                      {s.conversion != null && (
                        <span className="ml-2 text-xs text-muted-foreground">{s.conversion}%</span>
                      )}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded bg-muted">
                    <div className="h-2 rounded bg-primary" style={{
                      width: `${width}%`, opacity: 1 - i * 0.15,
                    }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Regional comparison (national roles only) */}
      {Array.isArray(data.regions) && data.regions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Regions, best to worst</CardTitle>
            <CardDescription>Sorted by portfolio at risk</CardDescription>
          </CardHeader>
          <CardContent>
            {data.regions.map((r: Row) => (
              <div key={r.region_id}
                   className="flex items-center justify-between border-b py-2 text-sm last:border-0">
                <span className="font-medium">{r.name}</span>
                <span className="text-muted-foreground">{r.active_loans} loans</span>
                <span className="tabular-nums">{kwacha(r.outstanding)}</span>
                <span className="tabular-nums text-muted-foreground">{kwacha(r.arrears)} owed</span>
                <span className={`tabular-nums font-medium ${
                  r.par30 == null ? "text-muted-foreground"
                    : r.par30 > 5 ? "text-destructive" : "text-success"}`}>
                  PAR {r.par30 == null ? "—" : `${r.par30.toFixed(1)}%`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
